import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { Company } from "../../store/types";
import { companyColumns } from "../../components/DatatableTemplates/CompanyTableTemplates";
import { Container } from "../../components/Container/Container";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import CompanyDialogs from "../../components/Dialogs/CompanyDialogs";
import { H2 } from "../../styles/Typography";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { IconPlus, IconSearch } from "../../assets/icons/Icon";
import {
  PageName,
  FlexLineJustify,
  FlexLineGapSmall,
  Label,
  InputContainer,
  Tag,
} from "../../styles/PageLayoutStyles.styles";

const Companies: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const {
    handlePostCompany,
    handleUpdateCompany,
    handleDeleteCompany,
    companies,
    refetchData,
    toastRef,
  } = useCompaniesAPI();
  const { users } = useUsersAPI();
  const { licenses } = useLicensesAPI();
  const [tableData, setTableData] = useState<Company[] | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>();
  const [searchValue, setSearchValue] = useState<string>("");
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<"create" | "edit" | "delete">(
    "create"
  );

  useEffect(() => {
    if (companies && users && licenses) {
      const formatted = companies.map((company: Company) => {
        const companyUsersCount = users.filter(
          (user) => user.organizationId === company.id
        ).length;
        const companyLicensesCount = licenses.filter(
          (lic) => lic.organizationId === company.id
        ).length;
        return {
          ...company,
          phone: company.phone
            ? String(company.phone).replace(/\D/g, "").slice(-10)
            : company.phone,
          users: companyUsersCount,
          licenses: companyLicensesCount,
        };
      });
      setTableData(formatted);
    } else {
      setTableData(null);
    }
  }, [companies, users, licenses]);

  const filteredData = useMemo(() => {
    if (!searchValue.trim() || !tableData) return tableData;
    const lowerSearch = searchValue.toLowerCase();
    return tableData.filter((company) => {
      return (
        company.name?.toLowerCase().includes(lowerSearch) ||
        company.users?.toString().includes(lowerSearch) ||
        company.licenses?.toString().includes(lowerSearch) ||
        company.phone?.toLowerCase().includes(lowerSearch) ||
        company.email?.toString().includes(lowerSearch)
      );
    });
  }, [tableData, searchValue]);

  const handleView = (company: Company) => {
    navigate(`/companies/${company.id}`);
  };

  const handleCreate = () => {
    setSelectedCompany(null);

    if (isMobile) {
      navigate("/companies/form?mode=create");
    } else {
      setDialogType("create");
      setDialogVisible(true);
    }
  };

  const handleEdit = (company: Company) => {
    setSelectedCompany(company);

    if (isMobile) {
      navigate(`/companies/form?mode=edit&id=${company.id}`);
    } else {
      setDialogType("edit");
      setDialogVisible(true);
    }
  };

  const handleDelete = (company: Company) => {
    setSelectedCompany(company);

    if (isMobile) {
      navigate(`/companies/form?mode=delete&id=${company.id}`);
    } else {
      setDialogType("delete");
      setDialogVisible(true);
    }
  };

  const handleDialogHide = () => {
    setDialogVisible(false);
  };

  const handleSelectionChange = (selected: Company | null) => {
    setSelectedCompany(selected);
  };

  return (
    <Container>
      <Toast ref={toastRef} position="bottom-center" />
      <FlexLineJustify>
        <PageName>
          <H2>Компании</H2>
          <Tag>
            {isMobile ? "" : "Количество:"}{" "}
            {companies?.length.toString() ?? "..."}
          </Tag>
        </PageName>

        <FlexLineGapSmall>
          <Button onClick={handleCreate}>
            <IconPlus size={20} />
            <Label>Новая компания</Label>
          </Button>
          <InputContainer>
            <CustomInputText
              value={searchValue}
              setValue={setSearchValue}
              placeholder="Поиск..."
              icon={<IconSearch size={20} />}
            />
          </InputContainer>
        </FlexLineGapSmall>
      </FlexLineJustify>
      <CustomDatatable<Company>
        data={filteredData ?? []}
        columns={companyColumns}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRowDoubleClick={handleView}
        selected={selectedCompany}
        onSelectionChange={handleSelectionChange}
        getRowId={(company) => company.id}
        sortable
      />
      <CompanyDialogs
        visible={dialogVisible}
        onHide={handleDialogHide}
        dialogType={dialogType}
        company={selectedCompany!}
        handleAddCompany={handlePostCompany}
        handleEditCompany={handleUpdateCompany}
        handleDeleteCompany={handleDeleteCompany}
        refetchData={refetchData}
      />
    </Container>
  );
};

export default Companies;
